// src/components/TaskList.tsx
import React, { useEffect, useState, useCallback } from "react";
import { Task } from "../types/task";
import { TaskStatus, TaskPriority } from "../types/enum";
import TaskService from "../services/task.service";

interface TaskListProps {
  onTaskSelectedForEdit: (task: Task) => void;
  selectedTask: Task | null;
  clearSelectedTask: () => void;
  tasksChanged: number;
}

const TaskList: React.FC<TaskListProps> = ({
  onTaskSelectedForEdit,
  selectedTask,
  clearSelectedTask,
  tasksChanged,
}) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [filterStatus, setFilterStatus] = useState<TaskStatus | "">("");
  const [filterPriority, setFilterPriority] = useState<TaskPriority | "">("");
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  // Add state for pagination
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const pageSize: number = 5; // Hardcode or make configurable

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Pass pagination parameters to the service call
      const pageData = await TaskService.getAllTasks(
        filterStatus === "" ? undefined : filterStatus,
        filterPriority === "" ? undefined : filterPriority,
        searchKeyword === "" ? undefined : searchKeyword,
        currentPage, // Pass current page
        pageSize // Pass page size
      );
      // Access the 'content' array from the pageData
      setTasks(pageData.content);
      setTotalPages(pageData.totalPages);
    } catch (err: any) {
      setError(
        err.message ||
          "Failed to load tasks. Please ensure you are logged in and backend is running."
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority, searchKeyword, currentPage]); // Add currentPage to dependencies

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks, tasksChanged]);

  const handleUpdateStatus = async (task: Task, newStatus: TaskStatus) => {
    if (task.id === undefined) return;
    try {
      const taskToUpdate: Omit<
        Task,
        "id" | "createdAt" | "updatedAt" | "userId"
      > = {
        name: task.name,
        description: task.description,
        status: newStatus,
        priority: task.priority,
        dueDate: task.dueDate,
      };
      await TaskService.updateTask(task.id, taskToUpdate);
      fetchTasks(); // Re-fetch tasks after update
    } catch (err: any) {
      setError(err.message || "Failed to update task status.");
      console.error(err);
      fetchTasks();
    }
  };

  const handleDelete = async (id: number | undefined) => {
    if (
      id === undefined ||
      !window.confirm("Are you sure you want to delete this task?")
    )
      return;
    try {
      await TaskService.deleteTask(id);
      fetchTasks(); // Re-fetch tasks after delete
      if (selectedTask && selectedTask.id === id) {
        clearSelectedTask();
      }
    } catch (err: any) {
      setError(err.message || "Failed to delete task.");
      console.error(err);
      fetchTasks();
    }
  };

  const handleEditClick = (task: Task) => {
    onTaskSelectedForEdit(task);
  };

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="card mt-4">
      <div className="card-header d-flex flex-column flex-md-row justify-content-between align-items-center">
        <h2 className="mb-2 mb-md-0">My Tasks</h2>

        <div className="d-flex flex-column flex-md-row align-items-center space-y-2 space-md-x-3">
          <div className="form-group mb-0 mr-md-2">
            <label htmlFor="filterStatus" className="mr-2 sr-only">
              Status:
            </label>
            <select
              id="filterStatus"
              className="form-control"
              value={filterStatus}
              onChange={(e) =>
                setFilterStatus(e.target.value as TaskStatus | "")
              }
            >
              <option value="">All Statuses</option>
              {Object.values(TaskStatus).map((status) => (
                <option key={status} value={status}>
                  {status.replace("_", " ")}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group mb-0 mr-md-2">
            <label htmlFor="filterPriority" className="mr-2 sr-only">
              Priority:
            </label>
            <select
              id="filterPriority"
              className="form-control"
              value={filterPriority}
              onChange={(e) =>
                setFilterPriority(e.target.value as TaskPriority | "")
              }
            >
              <option value="">All Priorities</option>
              {Object.values(TaskPriority).map((priority) => (
                <option key={priority} value={priority}>
                  {priority}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group mb-0">
            <label htmlFor="searchKeyword" className="sr-only">
              Search:
            </label>
            <input
              type="text"
              id="searchKeyword"
              className="form-control"
              placeholder="Search tasks..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
            />
          </div>
        </div>
      </div>

      <ul className="list-group list-group-flush">
        {loading ? (
          <li className="list-group-item text-center">Loading tasks...</li>
        ) : error ? (
          <li className="list-group-item text-danger">Error: {error}</li>
        ) : tasks.length === 0 ? (
          <li className="list-group-item">No tasks found for your criteria.</li>
        ) : (
          tasks.map((task) => (
            <li
              key={task.id}
              className="list-group-item d-flex justify-content-between align-items-center"
            >
              <div>
                <h5
                  className={
                    task.status === TaskStatus.COMPLETED ? "text-muted" : ""
                  }
                >
                  {task.name}
                </h5>
                {task.description && (
                  <p className="mb-1 text-muted">{task.description}</p>
                )}
                <small className="text-info">
                  Status: {task.status.replace("_", " ")}
                </small>
                <br />
                <small className="text-secondary">
                  Priority: {task.priority}
                </small>
                <br />
                {task.dueDate && (
                  <small className="ml-3 text-secondary">
                    Due: {task.dueDate}
                  </small>
                )}
              </div>
              <div>
                {task.status !== TaskStatus.COMPLETED && (
                  <button
                    className="btn btn-sm btn-success mr-2"
                    onClick={() =>
                      handleUpdateStatus(task, TaskStatus.COMPLETED)
                    }
                  >
                    Mark Complete
                  </button>
                )}
                <button
                  className="btn btn-sm btn-warning mr-2"
                  onClick={() => handleEditClick(task)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(task.id)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))
        )}
      </ul>

      {/* Pagination Controls */}
      {!loading && !error && totalPages > 0 && (
        <div className="card-footer d-flex justify-content-center">
          <button
            className="btn btn-secondary mr-2"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            Previous
          </button>
          <span className="align-self-center">
            Page {currentPage + 1} of {totalPages}
          </span>
          <button
            className="btn btn-secondary ml-2"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage + 1 >= totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default TaskList;
